import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Snowflake, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/sections/SectionHeader";
import { PRODUCTS } from "@/data/content";

export default function ProductCarousel() {
  const trackRef = useRef(null);
  const scroll = (dir) => {
    if (!trackRef.current) return;
    const amount = trackRef.current.clientWidth * 0.8;
    trackRef.current.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section data-testid="products-carousel" className="section-pad bg-surface-2/40 border-y border-border">
      <div className="max-w-7xl mx-auto container-px">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <SectionHeader
            eyebrow="Produse recomandate"
            title="Selecția pe care o poți pune azi în meniu."
            subtitle="Produse gândite pentru cafenele, restaurante, pizzerii și retail. Gust, constanță și aspect care vinde."
            testId="products-carousel-header"
          />
          <div className="flex items-center gap-2 self-end">
            <button data-testid="carousel-prev" onClick={() => scroll(-1)} aria-label="Anterior" className="h-11 w-11 rounded-full border border-border hover:border-gold transition-colors flex items-center justify-center">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button data-testid="carousel-next" onClick={() => scroll(1)} aria-label="Următor" className="h-11 w-11 rounded-full border border-border hover:border-gold transition-colors flex items-center justify-center">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div ref={trackRef} className="mt-12 flex gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-thin pb-4 -mx-5 px-5">
          {PRODUCTS.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 6) * 0.06 }}
              className="snap-start shrink-0 w-[280px] md:w-[320px]"
              data-testid={`product-card-${p.id}`}
            >
              <div className="product-card h-full flex flex-col">
                <div className="relative h-64 overflow-hidden">
                  <img src={p.image} alt={p.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute top-3 left-3">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-medium backdrop-blur ${p.type === "congelat" ? "bg-[#0b0b0f]/70 text-blue-200 border border-blue-300/30" : "bg-[#0b0b0f]/70 text-amber-200 border border-amber-300/30"}`}>
                      {p.type === "congelat" ? <Snowflake className="h-3 w-3" /> : <Flame className="h-3 w-3" />}
                      {p.type}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] bg-black/60 text-white/90 border border-white/20">
                    {p.weight}
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{p.category}</p>
                  <h3 className="mt-2 font-display text-xl text-text">{p.name}</h3>
                  <p className="mt-2 text-sm text-text-soft leading-relaxed flex-1">{p.benefit}</p>
                  <Link to="/contact" className="mt-5">
                    <Button variant="outline" className="w-full rounded-full border-border hover:border-gold hover:text-gold transition-colors" data-testid={`product-cta-${p.id}`}>
                      Solicită detalii
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
